import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Header from './product_cards_v2/Header';
import { getProductDetails } from '../../api/api';
import { copySharableLink } from '../../utils/utils';
import { DishTypes as ProductDetailDishtypes } from '../../../../../components/product/ProductDetail/DishTypes/DishTypes';
import Slider from 'react-slick';
import CookingInstructions from '../../../../../components/product/CookingInstructions/CookingInstructions';
import { getCustomer } from '@hungryroot/rest/api/get_customer/get/get-customer';
import { getProduct } from '@hungryroot/rest/api/get_product/get/get-product';
import { windowVars } from '@hungryroot/environment';
import { getWarningText } from '@hungryroot/dietary-restrictions';
import { PDPAddRemoveButton } from '../../../../../components/PDP/PDPAddRemoveButton/PDPAddRemoveButton';
import { TransparentButton } from '@hungryroot/ramen';
import { useAddToCartModal } from '../../../../../pages/my-hungryroot/components/AddToCartModal/AddToCartModalHook';
import { WarningText, WarningTextStyleVariant } from '@hungryroot/pho';
import { PeriodicityTypes } from '@hungryroot/periodicities';
import { useIdentify, AuthState } from '@hungryroot/customer';
import { Loading } from '@hungryroot/loading-component-primitives';

const getWarning = (dietaryProductIngredients, periodicity) => {
  const containsDietaryIngredients = dietaryProductIngredients.length > 0;
  const isNeverItem = periodicity === PeriodicityTypes.NEVER;

  if (containsDietaryIngredients) {
    return {
      warningText: `This item contains ${getWarningText(
        dietaryProductIngredients
      )}`,
      warningTextIntensity: WarningTextStyleVariant.Critical,
    };
  } else if (isNeverItem) {
    return {
      warningText: `You set this item to 'never'`,
      warningTextIntensity: WarningTextStyleVariant.Moderate,
    };
  }
  return null;
};

const PDP_v2 = (props) => {
  const {
    closeModal,
    onRemoveClick,
    isCustomizationPage,
    onAddClick,
    dietaryProductIngredients = [],
  } = props;
  const [product, setProduct] = useState(props.product);
  const [loading, setLoading] = useState(true);
  const { customerData, authState } = useIdentify();

  const { orderId } = useAddToCartModal();
  useEffect(() => {
    if (product) {
      if (!windowVars.isPrecheckout() && customerData?.id) {
        getProduct({
          productId: product.id,
          includes: 'all',
          customerId: customerData?.id,
          orderId,
        }).then((res) => {
          if (res.status !== 200) return;
          // defaulting to true here in case the value is undefined
          setProduct({
            ...res.data,
            is_merchandized: res?.data?.is_merchandized !== false,
          });
          setLoading(false);
        });
        return;
      }
      getProductDetails(product.id)
        .then((productDetails) => {
          setProduct(productDetails);
          setLoading(false);
        })
        .catch(console.error);
    }
  }, [customerData?.id]);

  if (
    loading ||
    !product ||
    props.loading ||
    authState === AuthState.isLoading
  ) {
    return <Loading variant="translucent-popover" />;
  }

  const cardImgUrl = product.card_image_url;
  const packagedImgUrl = product.packaged_image2_url;
  const productLink = `${window.location.origin}/products/${product.slug}/`;
  const readMoreLink = product.is_third_party
    ? '<br/>Information provided by the brand.<br/> <a class="hr-neutral_blue-color hr-underline" href="/terms#third-party">Read more</a>'
    : '';
  const settings = {
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    edgeFriction: 0.1,
    speed: 150,
    touchThreshold: 50,
    arrows: true,
    dots: true,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 800,
        settings: {
          arrows: false,
        },
      },
    ],
  };

  const warning = getWarning(dietaryProductIngredients, product.periodicity);
  const testid = `pdp-${product.id}`;

  return (
    <div data-testid={testid} className={'rdp-customize pdp-modal'}>
      <Slider {...settings}>
        <img src={cardImgUrl} alt={product.name} />
        {packagedImgUrl ? (
          <img
            src={packagedImgUrl}
            className={'packaged-img'}
            alt={product.name}
          />
        ) : null}
      </Slider>
      <div className="info-container">
        {warning && (
          <div className={'warning-text-container'}>
            <WarningText
              intensity={warning.warningTextIntensity}
              testid="warning-text"
            >
              {warning.warningText}
            </WarningText>
          </div>
        )}
        <Header
          product={product}
          isCustomizationPage={isCustomizationPage}
          onPeriodicityChange={(value) =>
            setProduct((prev) => ({ ...prev, periodicity: value }))
          }
          headerClass={`${warning ? 'header-container' : ''}`}
        />

        <div className={'body-holder'}>
          <h3 dangerouslySetInnerHTML={{ __html: product.description }}></h3>

          <div className="hr-line-separator"></div>

          {product.instruction && (
            <CookingInstructions
              testid="pdp-cooking-instructions"
              instructions={product.instruction}
            />
          )}

          <div>
            <span className="hr-paragraph-bold">Ingredients</span>
            <div
              className="product-details-text"
              dangerouslySetInnerHTML={{
                __html: product.ingredients + readMoreLink,
              }}
            ></div>
          </div>

          <div className="hr-line-separator"></div>

          <div>
            <span className="hr-paragraph-bold">Nutrition</span>
            <div
              className="product-details-text"
              dangerouslySetInnerHTML={{ __html: product.nutrition_html }}
            ></div>
          </div>

          <div className="hr-line-separator"></div>

          <div>
            <span className="hr-paragraph-bold">Tip</span>
            <div
              className="product-details-text"
              dangerouslySetInnerHTML={{ __html: product.remark }}
            ></div>
          </div>

          <div className="hr-line-separator"></div>

          {product.dish_types.length ? (
            isCustomizationPage ? (
              <ProductDetailDishtypes
                testid={`${testid}-dishtypes`}
                product={product}
              />
            ) : (
              <ProductDetailDishtypes
                testid={`${testid}-dishtypes`}
                link
                product={product}
              />
            )
          ) : null}

          <div
            className="share-container hr-mb-8"
            onClick={(evt) => copySharableLink(evt)}
          >
            <div className="share-link-container">
              <div className="link-text">
                <input
                  className="share-link hr-subtitle-md"
                  name="link"
                  value={productLink}
                  readOnly={true}
                />
              </div>
              <div className="copy-button hr-bold">Copy</div>
            </div>
          </div>

          {isCustomizationPage ? (
            <PDPAddRemoveButton
              product={product}
              dietaryProductIngredients={dietaryProductIngredients}
              onRemoveClick={() => {
                onRemoveClick && onRemoveClick();
                closeModal();
              }}
              onAddClick={onAddClick}
            />
          ) : null}
        </div>
        {closeModal && (
          <TransparentButton
            autoFocus
            className="close"
            onClick={closeModal}
            testid="pdp-modal-close"
          ></TransparentButton>
        )}
        {!isCustomizationPage ? (
          <div className="btm-liner-gradient"></div>
        ) : null}
      </div>
    </div>
  );
};

PDP_v2.propTypes = {
  closeModal: PropTypes.func,
  product: PropTypes.object,
  isCustomizationPage: PropTypes.bool,
  dietaryProductIngredients: PropTypes.array,
};

export default PDP_v2;
